import { useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { MainLayout } from '@/shared/components/MainLayout';
import { useAppDispatch } from '@/shared/hooks/redux';
import { checkAuth } from '@/shared/store/authSlice';
import { publicRoutes } from '@/app/routes/public.routes';
import { studentRoutes } from '@/app/routes/student.routes';
import { adminRoutes } from '@/app/routes/admin.routes';
import { authRoutes } from '@/app/routes/auth.routes';
import { AppErrorBoundary } from '@/shared/components/AppErrorBoundary';
import { AnalyticsTracker } from '@/shared/analytics/AnalyticsTracker';

const RootWithTracker = ({ children }: { children: React.ReactNode }) => (
  <>
    <AnalyticsTracker />
    {children}
  </>
);

// `RootWithTracker` e `errorElement` vivem por branch: todo branch fora do `/` precisa repetir os dois.
const withRootShell = (route: RouteObject): RouteObject => ({
  ...route,
  element: <RootWithTracker>{route.element}</RootWithTracker>,
  errorElement: <AppErrorBoundary />,
});

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <RootWithTracker>
        <MainLayout />
      </RootWithTracker>
    ),
    errorElement: <AppErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/home" replace /> },
      ...publicRoutes,
      ...studentRoutes,
    ],
  },
  // Admin routes use their own layout (AdminLayout with sidebar)
  ...adminRoutes.map(withRootShell),
  // Auth tem o próprio layout (AuthLayout, split, sem header/footer)
  ...authRoutes.map(withRootShell),
]);

export const AppRouter = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  return <RouterProvider router={router} />;
};
